/**
 * The categories global search can return, and how to present them.
 *
 * Single source of truth for the header dropdown, the full results page, and the
 * Ctrl+K palette — all three used to declare their own copy, so adding a category
 * to the backend meant remembering to add it in three places or it silently
 * vanished from one of the surfaces.
 *
 * Order matters: it's the order results are grouped in.
 */
export type SearchCategory =
    | "contact"
    | "organization"
    | "asset"
    | "mission"
    | "missionRequest"
    | "lead"
    | "invoice"
    | "bill"
    | "payment"
    | "creditNote"
    | "journalEntry"
    | "task";

export const CATEGORY_CONFIG: Record<SearchCategory, { label: string; icon: string }> = {
    contact: { label: "Contacts", icon: "bi-person" },
    organization: { label: "Organizations", icon: "bi-building" },
    asset: { label: "Assets", icon: "bi-truck" },
    mission: { label: "Missions", icon: "bi-globe2" },
    missionRequest: { label: "Mission Requests", icon: "bi-send" },
    lead: { label: "Leads", icon: "bi-funnel" },
    invoice: { label: "Invoices", icon: "bi-receipt" },
    bill: { label: "Bills", icon: "bi-receipt-cutoff" },
    payment: { label: "Payments", icon: "bi-cash-coin" },
    creditNote: { label: "Credit Notes", icon: "bi-arrow-counterclockwise" },
    journalEntry: { label: "Journal Entries", icon: "bi-journal-bookmark" },
    task: { label: "Tasks", icon: "bi-check2-square" },
};

/**
 * Organizations lead: the search is overwhelmingly "a service in a country"
 * or a provider's name, and a contact found through their organization
 * belongs after that organization.
 */
export const CATEGORY_ORDER: SearchCategory[] = [
    "organization",
    "contact",
    "asset",
    "mission",
    "missionRequest",
    "lead",
    "invoice",
    "bill",
    "payment",
    "creditNote",
    "journalEntry",
    "task",
];

/** One row returned by extra/quick-search or extra/full-search. */
export interface SearchResultItem {
    id: number;
    name: string;
    referenceNumber?: string;
    parent: string;
    phoneNumber?: string;
    email?: string;
    note?: string;
    url: string;
    type: SearchCategory;
    /** Why a geo search surfaced this row, e.g. "Services in Ramallah". Superseded by matchContext. */
    geoContext?: string | null;
    /**
     * Why the search surfaced this row — every reason, as chips: the service
     * it offers, its relation to the place, its specialty, "via" its
     * organization (contacts), or a partial-match flag.
     */
    matchContext?: MatchContextChip[];
    /** strict (every understood part holds) | literal (the whole text as a name) | partial (one part only). */
    matchKind?: MatchKind;
    /** Sub-heading within the category when the query was understood ("Offers Medical Evacuation — not in Jordan"). */
    matchGroup?: string | null;
    /** Soft-deleted record — rendered in a separate "Inactive records" section. */
    inactive?: boolean;
}

export type MatchKind = "strict" | "literal" | "partial";

/** What the backend understood from the text, and what the returned rows add up to. */
export interface SearchSummary {
    text: string;
    structured: boolean;
    understood: {
        place: string | null;
        placeText: string | null;
        service: string | null;
        serviceText: string | null;
        serviceNames: string[];
        text: string[];
        /** Aliases substituted before classification ("KSA" → "Saudi Arabia"). */
        aliases?: Array<{ from: string; to: string }>;
        /** Kind of record the query named ("Missions"), and the word as typed. */
        module?: string | null;
        moduleText?: string | null;
        /** Period the query named ("April 2026"), and the phrase as typed. */
        date?: string | null;
        dateText?: string | null;
    };
    counts: {
        full: number;
        literal: number;
        partial: number;
        partialOfferingService: number;
        partialRelatedToPlace: number;
    };
    /** One sentence on the outcome; null when the text was plain (nothing understood). */
    headline: string | null;
    /** What the partial / literal rows are, when there is no full match. */
    detail: string | null;
    /** One-click wider queries when nothing satisfied all of it: `text` is what to search. */
    alternatives?: Array<{ label: string; text: string }>;
    /** The closest providers offering the service, when nobody offers it in the place. */
    nearest?: NearestCoverage[];
}

/** A completion for the word being typed, from extra/search-suggest. */
export interface SearchSuggestion {
    kind: "place" | "service" | "alias";
    label: string;
    detail: string | null;
    /** The full query to run when picked. */
    text: string;
}

export const SUGGESTION_ICON: Record<SearchSuggestion["kind"], string> = {
    place: "bi-geo-alt",
    service: "bi-tools",
    alias: "bi-arrow-left-right",
};

export type QueryPart = "place" | "service" | "text" | "module" | "date";

/**
 * The query with one understood part removed — the × on a banner chip.
 * A part that reached the parser through an alias is removed as typed.
 */
export function removeQueryPart(summary: SearchSummary, part: QueryPart): string {
    const { understood } = summary;
    let phrases: string[] = [];
    if (part === "place" && understood.placeText) phrases = [understood.placeText];
    if (part === "service" && understood.serviceText) phrases = [understood.serviceText];
    if (part === "module" && understood.moduleText) phrases = [understood.moduleText];
    if (part === "date" && understood.dateText) phrases = [understood.dateText];
    if (part === "text") phrases = understood.text;
    const aliases = understood.aliases ?? [];
    phrases = phrases.map(phrase => aliases.find(alias => alias.to.toLowerCase() === phrase.toLowerCase())?.from ?? phrase);

    let text = summary.text;
    for (const phrase of phrases) {
        const escaped = phrase.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
        text = text.replace(new RegExp(`(^|\\s)"?${escaped}"?(?=\\s|$)`, "i"), "$1");
    }
    return text.replace(/\s+/g, " ").trim();
}

/** How many understood parts a summary carries — the × only shows when there is something left to search. */
export const understoodPartCount = (summary: SearchSummary): number =>
    (summary.understood.place ? 1 : 0) + (summary.understood.service ? 1 : 0) + (summary.understood.text.length > 0 ? 1 : 0)
    + (summary.understood.module ? 1 : 0) + (summary.understood.date ? 1 : 0);

/** A provider offering the searched service closest to the searched place. */
export interface NearestCoverage {
    organizationId: number;
    name: string;
    url: string;
    place: string;
    distanceKm: number;
}

/** Body of extra/quick-search and extra/full-search. */
export interface SearchResponseBody {
    data: SearchResultItem[];
    summary?: SearchSummary;
}

/** Why one list row is here — see the backend's RowMatch. */
export interface RowMatch {
    chips: MatchContextChip[];
    kind: MatchKind;
    group: string | null;
}

/**
 * What a LIST search sends back beside `{ data, count }` when the keyword was
 * understood: the summary for the banner under the omni-box, and per row id
 * the chips explaining that row.
 */
export interface ListSearchInfo {
    summary: SearchSummary;
    matches: Record<string, RowMatch>;
}

/** Whether the summary has anything worth a banner: something understood, or a verdict. */
export const summaryHasContent = (summary: SearchSummary | null | undefined): summary is SearchSummary =>
    !!summary && (summary.headline != null || summary.understood.place != null || summary.understood.service != null);

/** Items of one category split under their sub-headings, in the backend's rank order. */
export interface ResultSubgroup {
    group: string | null;
    items: SearchResultItem[];
}

/**
 * Split a category's rows under their `matchGroup` sub-headings. A category
 * whose rows are all full matches (or carry no group) needs no sub-heading.
 */
export const subgroupResults = (items: SearchResultItem[]): ResultSubgroup[] => {
    const groups: ResultSubgroup[] = [];
    for (const item of items) {
        const group = item.matchGroup ?? null;
        const last = groups[groups.length - 1];
        if (last && last.group === group) last.items.push(item);
        else groups.push({ group, items: [item] });
    }
    const distinct = new Set(groups.map(g => g.group));
    if (distinct.size <= 1 && (groups[0]?.group == null || groups[0].group === "Full matches")) {
        return [{ group: null, items }];
    }
    return groups;
};

export type MatchContextKind = "place" | "service" | "specialty" | "via" | "missing" | "partial" | "proven" | "rating" | "date";

/** Chips that mark a weaker (partial) relation — rendered muted. */
export const isWeakChip = (kind: MatchContextKind): boolean => kind === "missing" || kind === "partial";

/** One "why this matched" chip on a result row. */
export interface MatchContextChip {
    kind: MatchContextKind;
    label: string;
}

/** Bootstrap icon per chip kind. */
export const MATCH_CONTEXT_ICON: Record<MatchContextKind, string> = {
    place: "bi-geo-alt",
    service: "bi-tools",
    specialty: "bi-award",
    via: "bi-building",
    missing: "bi-x-circle",
    partial: "bi-dash-circle",
    proven: "bi-check2-circle",
    rating: "bi-star-fill",
    date: "bi-calendar-range",
};

/** Tooltip per chip kind. */
export const MATCH_CONTEXT_TITLE: Record<MatchContextKind, string> = {
    place: "How this record relates to the place you searched",
    service: "A service this record offers that matches your search",
    specialty: "The organization's specialty matches your search",
    via: "Matched through this contact's organization",
    missing: "The part of your search this record does NOT satisfy — shown after the full matches",
    partial: "Only part of your search matched — shown after the full matches",
    proven: "Completed, non-cancelled missions this organization took part in — in the searched place when there is one",
    rating: "Operational rating given by dispatchers after missions",
    date: "The period you searched — this record falls in it",
};

/**
 * The chips to render for a result: matchContext when the backend sent it,
 * else the older single geoContext chip.
 */
export const matchChips = (item: Pick<SearchResultItem, "matchContext" | "geoContext">): MatchContextChip[] => {
    if (item.matchContext && item.matchContext.length > 0) return item.matchContext;
    return item.geoContext ? [{ kind: "place", label: item.geoContext }] : [];
};
